import React from 'react';
import {
  FileText,
  MessageCircle,
  Heart,
  Share2,
  Bookmark,
  MoreHorizontal,
  TrendingUp,
  Users,
  BarChart3,
  CheckCircle2,
  Clock,
  Sparkles,
  ExternalLink,
  ThumbsUp,
  MessageSquare,
  Repeat2,
  Award,
  Beaker,
  GraduationCap,
  HelpCircle,
  PieChart,
  ChevronRight,
  Flame,
  Eye,
  Zap,
  ArrowUpRight,
} from 'lucide-react';
import './FeedMockupContent.css';

const FeedMockupContent = ({ visiblePosts = 0, animationPhase }) => {
  // Rich feed data with different post types
  const feedPosts = [
    {
      id: 1,
      type: 'paper',
      author: {
        name: 'Dr. Lena Hoffmann',
        avatar: 'LH',
        institution: 'MIT AI Lab',
        verified: true,
        following: false,
      },
      timestamp: '2h ago',
      content: {
        text: 'Excited to share our latest research on transformer architectures for protein folding prediction. We achieved 94.2% accuracy on the CASP15 benchmark! 🧬',
        paper: {
          title: 'AlphaFold-X: Enhanced Protein Structure Prediction',
          journal: 'Nature Methods',
          year: '2026',
          citations: 127,
          isOpenAccess: true,
        },
      },
      engagement: {
        likes: 1247,
        comments: 89,
        shares: 234,
        reposts: 156,
      },
      tags: ['Deep Learning', 'Bioinformatics'],
      trending: true,
    },
    {
      id: 2,
      type: 'milestone',
      author: {
        name: 'Prof. Idris Malik',
        avatar: 'IM',
        institution: 'Berkeley EECS',
        verified: true,
        following: true,
      },
      timestamp: '3h ago',
      content: {
        text: '🎉 Thrilled to announce our lab received a $2.5M NSF grant for interpretable AI in climate modeling! Hiring 3 PhD students.',
        milestoneType: 'funding',
        amount: '$2.5M',
      },
      engagement: {
        likes: 2156,
        comments: 234,
        shares: 567,
        reposts: 312,
      },
      tags: ['Funding', 'Climate AI'],
      celebration: true,
    },
    {
      id: 3,
      type: 'question',
      author: {
        name: 'Callum Fraser',
        avatar: 'CF',
        institution: 'Stanford University',
        verified: false,
        following: true,
      },
      timestamp: '4h ago',
      content: {
        text: 'Has anyone implemented federated learning with differential privacy in healthcare? Looking for practical insights.',
        answerCount: 23,
        topAnswer: {
          author: 'Dr. Clara Whitfield',
          preview: 'We deployed a similar system at Johns Hopkins...',
        },
      },
      engagement: {
        likes: 342,
        comments: 23,
        shares: 67,
        reposts: 45,
      },
      tags: ['Federated Learning', 'Privacy'],
      hot: true,
    },
    {
      id: 4,
      type: 'poll',
      author: {
        name: 'ResearchPro',
        avatar: 'RP',
        institution: 'Official',
        verified: true,
        isOfficial: true,
      },
      timestamp: '5h ago',
      content: {
        text: 'Which emerging area will have the biggest impact in 2026?',
        options: [
          { text: 'Quantum ML', votes: 2341, percentage: 38 },
          { text: 'Neuromorphic', votes: 1876, percentage: 31 },
          { text: 'Sustainable AI', votes: 1234, percentage: 20 },
          { text: 'BCI', votes: 672, percentage: 11 },
        ],
        totalVotes: 6123,
        endsIn: '2 days',
        userVoted: true,
        userChoice: 0,
      },
      engagement: {
        likes: 892,
        comments: 156,
        shares: 312,
        reposts: 234,
      },
      tags: ['Research Trends'],
      featured: true,
    },
    {
      id: 5,
      type: 'research',
      author: {
        name: 'Dr. Amina Rashid',
        avatar: 'AR',
        institution: 'DeepMind',
        verified: true,
        following: false,
      },
      timestamp: '6h ago',
      content: {
        text: 'New findings: Emergent abilities in LLMs are not as sudden as previously thought. Our statistical analysis reveals gradual progression.',
        researchUpdate: {
          type: 'Breakthrough',
          field: 'NLP Research',
        },
      },
      engagement: {
        likes: 3421,
        comments: 267,
        shares: 892,
        reposts: 567,
      },
      tags: ['LLMs', 'AI Research'],
      trending: true,
    },
    {
      id: 6,
      type: 'milestone',
      author: {
        name: 'Dr. Ingrid Svensson',
        avatar: 'IS',
        institution: 'Oxford University',
        verified: true,
        following: false,
      },
      timestamp: '7h ago',
      content: {
        text: '📚 Just hit 10,000 citations on my work on attention mechanisms! Thank you to the amazing research community.',
        milestoneType: 'citation',
        amount: '10K',
      },
      engagement: {
        likes: 4521,
        comments: 312,
        shares: 234,
        reposts: 189,
      },
      tags: ['Milestone', 'Deep Learning'],
      celebration: true,
    },
    {
      id: 7,
      type: 'paper',
      author: {
        name: 'Dr. Jianwei Xu',
        avatar: 'JX',
        institution: 'Google Research',
        verified: true,
        following: true,
      },
      timestamp: '8h ago',
      content: {
        text: 'Our paper on efficient vision transformers got accepted at CVPR 2026! 40% faster inference with same accuracy.',
        paper: {
          title: 'EfficientViT: Fast Vision Transformers',
          journal: 'CVPR 2026',
          year: '2026',
          citations: 45,
          isOpenAccess: true,
        },
      },
      engagement: {
        likes: 1823,
        comments: 145,
        shares: 423,
        reposts: 278,
      },
      tags: ['Computer Vision', 'Transformers'],
      trending: true,
    },
    {
      id: 8,
      type: 'research',
      author: {
        name: 'Dr. Beatrice Khoury',
        avatar: 'BK',
        institution: 'Meta AI',
        verified: true,
        following: false,
      },
      timestamp: '9h ago',
      content: {
        text: 'Interesting discovery: Self-supervised learning on video data shows remarkable few-shot capabilities. Thread 🧵',
        researchUpdate: {
          type: 'Discovery',
          field: 'Self-Supervised',
        },
      },
      engagement: {
        likes: 2134,
        comments: 178,
        shares: 567,
        reposts: 345,
      },
      tags: ['SSL', 'Video AI'],
      hot: true,
    },
    {
      id: 9,
      type: 'repost',
      repostedBy: {
        name: 'Dr. Nikolai Petrov',
        avatar: 'NP',
      },
      author: {
        name: 'Dr. Elif Demir',
        avatar: 'ED',
        institution: 'OpenAI',
        verified: true,
        following: false,
      },
      timestamp: '10h ago',
      content: {
        text: 'Major breakthrough: Our new RLHF technique reduces training time by 60% while improving alignment scores. Paper dropping next week! 🔥',
      },
      engagement: {
        likes: 5672,
        comments: 423,
        shares: 1245,
        reposts: 892,
      },
      tags: ['RLHF', 'AI Safety'],
      trending: true,
    },
    {
      id: 10,
      type: 'paper',
      author: {
        name: 'Dr. Owen Gallagher',
        avatar: 'OG',
        institution: 'Anthropic',
        verified: true,
        following: true,
      },
      timestamp: '11h ago',
      content: {
        text: 'Constitutional AI just got better. Our latest paper shows how to scale safety without sacrificing capabilities.',
        paper: {
          title: 'Scaling Constitutional AI Methods',
          journal: 'NeurIPS 2026',
          year: '2026',
          citations: 67,
          isOpenAccess: true,
        },
      },
      engagement: {
        likes: 2891,
        comments: 198,
        shares: 534,
        reposts: 423,
      },
      tags: ['AI Safety', 'LLMs'],
      trending: true,
    },
  ];

  // Format large numbers
  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Render paper attachment
  const renderPaperAttachment = (paper) => (
    <div className="feed-paper-attachment">
      <div className="feed-paper-icon">
        <FileText size={14} />
      </div>
      <div className="feed-paper-info">
        <h5 className="feed-paper-title">{paper.title}</h5>
        <div className="feed-paper-meta">
          <span className="feed-paper-journal">{paper.journal}</span>
          <span className="feed-paper-dot">•</span>
          <span className="feed-paper-citations">
            <BarChart3 size={9} />
            {paper.citations}
          </span>
        </div>
      </div>
      {paper.isOpenAccess && (
        <span className="feed-paper-badge feed-paper-badge--open">
          Open
        </span>
      )}
      <button className="feed-paper-link">
        <ExternalLink size={10} />
      </button>
    </div>
  );

  // Render milestone content
  const renderMilestoneContent = (content) => (
    <div className="feed-milestone-content">
      <div className="feed-milestone-badge">
        {content.milestoneType === 'funding' && <Sparkles size={12} />}
        {content.milestoneType === 'citation' && <TrendingUp size={12} />}
        <span>{content.amount}</span>
      </div>
      <p className="feed-post-text">{content.text}</p>
    </div>
  );

  // Render research update content
  const renderResearchContent = (content) => (
    <div className="feed-research-content">
      <div className="feed-research-badge">
        <Beaker size={10} />
        <span>{content.researchUpdate.type}</span>
        <span className="feed-research-field">{content.researchUpdate.field}</span>
      </div>
      <p className="feed-post-text">{content.text}</p>
    </div>
  );

  // Render question content
  const renderQuestionContent = (content) => (
    <div className="feed-question-content">
      <div className="feed-question-header">
        <HelpCircle size={12} className="feed-question-icon" />
        <span className="feed-question-label">Question</span>
        <span className="feed-question-answers">
          {content.answerCount} answers
        </span>
      </div>
      <p className="feed-post-text">{content.text}</p>
      {content.topAnswer && (
        <div className="feed-top-answer">
          <div className="feed-top-answer-header">
            <CheckCircle2 size={10} />
            <span>Top: {content.topAnswer.author}</span>
          </div>
          <p className="feed-top-answer-preview">{content.topAnswer.preview}</p>
        </div>
      )}
    </div>
  );

  // Render poll content
  const renderPollContent = (content) => (
    <div className="feed-poll-content">
      <div className="feed-poll-header">
        <PieChart size={12} className="feed-poll-icon" />
        <span className="feed-poll-label">Poll</span>
        <span className="feed-poll-votes">{formatNumber(content.totalVotes)} votes</span>
      </div>
      <p className="feed-post-text feed-post-text--poll">{content.text}</p>
      <div className="feed-poll-options">
        {content.options.slice(0, 3).map((option, idx) => (
          <div 
            key={idx} 
            className={`feed-poll-option ${content.userVoted && content.userChoice === idx ? 'selected' : ''}`}
          >
            <div 
              className="feed-poll-bar" 
              style={{ width: `${option.percentage}%` }}
            ></div>
            <span className="feed-poll-option-text">{option.text}</span>
            <span className="feed-poll-option-percent">{option.percentage}%</span>
          </div>
        ))}
      </div>
    </div>
  );

  // Render post badges
  const renderPostBadges = (post) => (
    <div className="feed-post-badges">
      {post.trending && (
        <span className="feed-badge feed-badge--trending">
          <TrendingUp size={10} />
          Trending
        </span>
      )}
      {post.hot && (
        <span className="feed-badge feed-badge--hot">
          <Flame size={10} />
          Hot
        </span>
      )}
      {post.featured && (
        <span className="feed-badge feed-badge--featured">
          <Sparkles size={10} />
          Featured
        </span>
      )}
      {post.celebration && (
        <span className="feed-badge feed-badge--celebration">
          <Award size={10} />
          Milestone
        </span>
      )}
    </div>
  );

  return (
    <div className="feed-mockup-content">
      {/* Feed Header */}
      <header className="feed-header">
        <div className="feed-header-left">
          <div className="feed-title-row">
            <h2 className="feed-page-title">Research Feed</h2>
            <div className="feed-title-badge">
              <Zap size={10} />
              <span>Live Updates</span>
            </div>
          </div>
          <p className="feed-page-subtitle">Stay updated with the latest from your network</p>
        </div>
        <div className="feed-header-actions">
          <button className="feed-filter-btn">
            <Sparkles size={12} />
            <span>For You</span>
          </button>
        </div>
      </header>

      {/* Feed Tabs */}
      <div className="feed-tabs-container">
        <div className="feed-tabs">
          <button className="feed-tab active">
            <Zap size={11} />
            <span>All</span>
          </button>
          <button className="feed-tab">
            <FileText size={11} />
            <span>Papers</span>
          </button>
          <button className="feed-tab">
            <Beaker size={11} />
            <span>Research</span>
          </button>
          <button className="feed-tab">
            <Award size={11} />
            <span>Milestones</span>
          </button>
          <button className="feed-tab">
            <HelpCircle size={11} />
            <span>Q&A</span>
          </button>
          <button className="feed-tab">
            <PieChart size={11} />
            <span>Polls</span>
          </button>
        </div>
        <div className="feed-live-indicator">
          <span className="feed-live-dot"></span>
          <span>12 new</span>
        </div>
      </div>

      {/* Feed Posts */}
      <div className="feed-posts-container">
        {feedPosts.map((post, index) => (
          <article 
            key={post.id}
            className={`feed-post feed-post--${post.type} ${index < visiblePosts ? 'visible' : ''}`}
            style={{ transitionDelay: `${index * 120}ms` }}
          >
            {/* Reposted By Header */}
            {post.type === 'repost' && post.repostedBy && (
              <div className="feed-reposted-by">
                <Repeat2 size={10} />
                <span>{post.repostedBy.name} reposted</span>
              </div>
            )}

            {/* Post Header */}
            <div className="feed-post-header">
              <div className="feed-author">
                <div className={`feed-avatar ${post.author.isOfficial ? 'feed-avatar--official' : ''}`}>
                  <span>{post.author.avatar}</span>
                  {post.author.verified && (
                    <div className="feed-verified">
                      <CheckCircle2 size={8} />
                    </div>
                  )}
                </div>
                <div className="feed-author-info">
                  <div className="feed-author-row">
                    <span className="feed-author-name">{post.author.name}</span>
                    {post.author.following && (
                      <span className="feed-following-badge">Following</span>
                    )}
                  </div>
                  <div className="feed-author-meta">
                    <span className="feed-institution">{post.author.institution}</span>
                    <span className="feed-meta-dot">•</span>
                    <span className="feed-timestamp">{post.timestamp}</span>
                  </div>
                </div>
              </div>
              <div className="feed-post-actions-top">
                {renderPostBadges(post)}
                <button className="feed-more-btn">
                  <MoreHorizontal size={14} />
                </button>
              </div>
            </div>

            {/* Post Content */}
            <div className="feed-post-content">
              {post.type === 'question' ? (
                renderQuestionContent(post.content)
              ) : post.type === 'poll' ? (
                renderPollContent(post.content)
              ) : post.type === 'milestone' ? (
                renderMilestoneContent(post.content)
              ) : post.type === 'research' ? (
                renderResearchContent(post.content)
              ) : (
                <>
                  <p className="feed-post-text">{post.content.text}</p>
                  {post.content.paper && renderPaperAttachment(post.content.paper)}
                </>
              )}
            </div>

            {/* Tags */}
            <div className="feed-tags">
              {post.tags.slice(0, 2).map((tag, idx) => (
                <span key={idx} className="feed-tag">#{tag}</span>
              ))}
            </div>

            {/* Compact Engagement & Actions */}
            <div className="feed-footer">
              <div className="feed-engagement-compact">
                <span className="feed-stat-compact">
                  <Heart size={10} className="feed-stat-icon--like" />
                  {formatNumber(post.engagement.likes)}
                </span>
                <span className="feed-stat-compact">
                  <MessageCircle size={10} />
                  {formatNumber(post.engagement.comments)}
                </span>
                <span className="feed-stat-compact feed-stat-compact--repost">
                  <Repeat2 size={10} />
                  {formatNumber(post.engagement.reposts || 0)}
                </span>
                <span className="feed-stat-compact">
                  <Share2 size={10} />
                  {formatNumber(post.engagement.shares)}
                </span>
              </div>
              <button className="feed-bookmark-btn">
                <Bookmark size={11} />
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default FeedMockupContent;
